/**
 * Comuna Loader v2.5.2
 *
 * Maneja la carga dinámica de comunas basada en la selección de región.
 * v2.5.1: Traduce textos JS de WC Blocks (toggle address_2).
 * v2.5.0: Reorganiza campos (Dirección+Apartamento, Región+Comuna).
 *
 * @package SuperPymeCheckoutBlocks
 */
(function() {
    'use strict';

    // Debug mode
    const DEBUG = window.location.search.includes('spcb_debug=1');
    const log = (...args) => DEBUG && console.log('[SPCB Comuna]', ...args);
    const isCheckoutProcessing = () => {
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            return true;
        }

        const checkoutStore = window.wp?.data?.select?.('wc/store/checkout');
        return !!(
            checkoutStore?.isProcessing?.() ||
            checkoutStore?.isBeforeProcessing?.() ||
            checkoutStore?.isAfterProcessing?.()
        );
    };

    const timers = (() => {
        const timeouts = new Map();
        const intervals = new Map();
        const pausedTimeouts = [];
        const pausedIntervals = [];
        let paused = false;

        const scheduleTimeout = (fn, delay, label) => {
            if (paused || isCheckoutProcessing()) {
                log('Timeout blocked - checkout processing:', label);
                return null;
            }

            const id = setTimeout(() => {
                timeouts.delete(id);

                if (paused || isCheckoutProcessing()) {
                    log('Timeout skipped - checkout processing:', label);
                    return;
                }

                fn();
            }, delay);

            timeouts.set(id, { fn, delay, label });
            return id;
        };

        const scheduleInterval = (fn, delay, label) => {
            if (paused || isCheckoutProcessing()) {
                log('Interval blocked - checkout processing:', label);
                return null;
            }

            const id = setInterval(() => {
                if (paused || isCheckoutProcessing()) {
                    log('Interval tick skipped - checkout processing:', label);
                    return;
                }

                fn();
            }, delay);

            intervals.set(id, { fn, delay, label });
            return id;
        };

        const clearManagedTimeout = (id) => {
            if (!id) return;
            clearTimeout(id);
            timeouts.delete(id);
        };

        const pause = () => {
            if (paused) return;
            paused = true;

            timeouts.forEach((meta, id) => {
                clearTimeout(id);
                pausedTimeouts.push(meta);
            });
            timeouts.clear();

            intervals.forEach((meta, id) => {
                clearInterval(id);
                pausedIntervals.push(meta);
            });
            intervals.clear();

            log('Timers paused - checkout processing');
        };

        const resume = () => {
            if (!paused) return;
            paused = false;

            while (pausedTimeouts.length) {
                const meta = pausedTimeouts.shift();
                if (meta?.fn) {
                    scheduleTimeout(meta.fn, 0, meta.label);
                }
            }

            while (pausedIntervals.length) {
                const meta = pausedIntervals.shift();
                if (meta?.fn) {
                    scheduleInterval(meta.fn, meta.delay, meta.label);
                }
            }

            log('Timers resumed - checkout processing ended');
        };

        return {
            scheduleTimeout,
            scheduleInterval,
            clearManagedTimeout,
            pause,
            resume
        };
    })();

    if (typeof window.spcbCheckoutLock?.subscribe === 'function') {
        window.spcbCheckoutLock.subscribe((processing) => {
            if (processing) {
                timers.pause();
            } else {
                timers.resume();
            }
        });
    }

    // Cache de comunas por región
    const comunasCache = {};

    // Flag para evitar múltiples inyecciones simultáneas
    let isInjecting = false;

    // Flag para rastrear si ya intercambiamos las posiciones
    const swappedFields = {
        shipping: false,
        billing: false
    };

    // Almacenar última región conocida para detectar cambios
    const lastKnownState = {
        shipping: null,
        billing: null
    };

    // Regiones de Chile para precargar
    const CHILE_REGIONS = [
        'CL-AI', 'CL-AN', 'CL-AP', 'CL-AR', 'CL-AT', 'CL-BI', 'CL-CO',
        'CL-LI', 'CL-LL', 'CL-LR', 'CL-MA', 'CL-ML', 'CL-NB', 'CL-RM',
        'CL-TA', 'CL-VS'
    ];

    /**
     * Setear valor en input React de forma segura
     */
    function safeSetReactInputValue(input, value, context = 'comuna-loader') {
        const helper = window.spcbCheckoutLock?.safeSetReactInputValue;
        if (typeof helper === 'function') {
            return helper(input, value, context);
        }

        if (!input) {
            log('safeSetReactInputValue blocked - missing input:', context);
            return false;
        }

        if (isCheckoutProcessing()) {
            log('safeSetReactInputValue blocked - checkout processing:', context, input.id || input.name);
            return false;
        }

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
        ).set;

        nativeInputValueSetter.call(input, value);

        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
    }

    /**
     * Crear el contenido del select de comunas (estructura WC Blocks)
     */
    function createComunaContent(cities, currentValue, onChange, addressType) {
        const container = document.createElement('div');
        container.className = 'wc-blocks-components-select__container spcb-comuna-container';

        // Siempre usar "Comuna" como label (ignorar label de WooCommerce que puede decir "Ciudad")
        const labelText = 'Comuna';

        const label = document.createElement('label');
        label.className = 'wc-blocks-components-select__label';
        label.setAttribute('for', `${addressType}-city-spcb`);
        label.textContent = labelText;

        const select = document.createElement('select');
        select.className = 'wc-blocks-components-select__select spcb-comuna-select';
        select.id = `${addressType}-city-spcb`;
        select.setAttribute('aria-invalid', 'false');
        select.required = true;
        select.size = 1;

        if (addressType === 'shipping') {
            select.classList.add('spcb-comuna-select-shipping');
        } else {
            select.classList.add('spcb-comuna-select-billing');
        }

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Seleccionar comuna';
        placeholder.disabled = true;
        select.appendChild(placeholder);

        let hasSelected = false;
        cities.forEach(city => {
            const option = document.createElement('option');
            option.value = city;
            option.textContent = city;
            if (city === currentValue) {
                option.selected = true;
                hasSelected = true;
            }
            select.appendChild(option);
        });

        if (!hasSelected) {
            placeholder.selected = true;
        }

        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('width', '24');
        svg.setAttribute('height', '24');
        svg.setAttribute('class', 'wc-blocks-components-select__expand');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M17.5 11.6L12 16l-5.5-4.4.9-1.2L12 14l4.5-3.6 1 1.2z');
        svg.appendChild(path);

        select.addEventListener('change', (e) => onChange(e.target.value));

        container.appendChild(label);
        container.appendChild(select);
        container.appendChild(svg);

        return container;
    }

    /**
     * Cargar comunas desde la API REST
     */
    async function loadComunas(regionCode) {
        if (!regionCode) return [];

        const cacheKey = regionCode;

        if (comunasCache[cacheKey]) {
            log('Comunas from cache:', cacheKey, comunasCache[cacheKey].length);
            return comunasCache[cacheKey];
        }

        try {
            const response = await fetch(
                `/wp-json/spcb/v1/comunas/${regionCode}?_cb=${Date.now()}`
            );

            if (!response.ok) {
                log('API error:', response.status);
                return [];
            }

            const data = await response.json();
            const comunas = data.comunas || data || [];

            comunasCache[cacheKey] = comunas;
            log('Comunas loaded:', cacheKey, comunas.length);

            return comunas;
        } catch (e) {
            log('Error loading comunas:', e);
            return [];
        }
    }

    /**
     * Precargar comunas para todas las regiones de Chile
     */
    async function preloadAllComunas() {
        log('Preloading all comunas...');
        const promises = CHILE_REGIONS.map(region => loadComunas(region));
        try {
            await Promise.all(promises);
            log('All comunas preloaded');
        } catch (e) {
            log('Error preloading comunas:', e);
        }
    }

    /**
     * Verificar si el formulario de dirección está visible
     */
    function isAddressFormVisible(addressType) {
        const cityInput = document.querySelector(`#${addressType}-city`);
        if (!cityInput) return false;

        const wrapper = cityInput.closest('.wc-block-components-address-form');
        if (!wrapper) return false;

        const style = window.getComputedStyle(wrapper);
        return style.display !== 'none' && style.visibility !== 'hidden';
    }

    /**
     * Obtener el wrapper del campo ciudad - CORREGIDO
     */
    function getCityWrapper(addressType) {
        // Buscar el input de ciudad específico para este addressType
        const cityInput = document.querySelector(`#${addressType}-city`);
        if (!cityInput) return null;

        // El wrapper es el ancestro con clase __city
        return cityInput.closest('.wc-block-components-address-form__city');
    }

    /**
     * Obtener el valor actual de la región
     */
    function getCurrentState(addressType) {
        const stateSelect = document.querySelector(`#${addressType}-state`);
        return stateSelect?.value || '';
    }

    /**
     * Reorganizar posición de los campos del formulario de dirección
     * Orden deseado:
     * - Fila 1: Nombre + Apellidos
     * - Fila 2: Dirección + Apartamento (address_1 + address_2)
     * - Fila 3: Región + Comuna (state + city)
     * - Fila 4: Teléfono
     */
    function swapFieldPositions(addressType) {
        // Evitar intercambios múltiples
        if (swappedFields[addressType]) {
            return false;
        }

        const form = document.querySelector(`#${addressType}`);
        if (!form) return false;

        // Buscar los wrappers específicos
        const address1Wrapper = document.querySelector(`#${addressType}-address_1`)?.closest('.wc-block-components-address-form__address_1');
        const address2Wrapper = document.querySelector(`#${addressType}-address_2`)?.closest('.wc-block-components-address-form__address_2');
        const stateWrapper = document.querySelector(`#${addressType}-state`)?.closest('.wc-block-components-address-form__state');
        const cityWrapper = document.querySelector(`#${addressType}-city`)?.closest('.wc-block-components-address-form__city');

        if (!stateWrapper || !cityWrapper) {
            return false;
        }

        let changed = false;

        // 1. Mover address_2 justo después de address_1 (si existe)
        if (address1Wrapper && address2Wrapper) {
            const addr1Index = Array.from(form.children).indexOf(address1Wrapper);
            const addr2Index = Array.from(form.children).indexOf(address2Wrapper);

            // Si address_2 no está justo después de address_1, moverlo
            if (addr2Index !== addr1Index + 1) {
                // Insertar address_2 después de address_1
                address1Wrapper.parentNode.insertBefore(address2Wrapper, address1Wrapper.nextSibling);
                log('Moved address_2 after address_1 for', addressType);
                changed = true;
            }
        }

        // 2. Mover state (Región) antes de city (Comuna)
        const stateIndex = Array.from(form.children).indexOf(stateWrapper);
        const cityIndex = Array.from(form.children).indexOf(cityWrapper);

        if (stateIndex > cityIndex) {
            // Mover el campo state (Región) antes del campo city (Comuna)
            cityWrapper.parentNode.insertBefore(stateWrapper, cityWrapper);
            log('Moved state before city for', addressType);
            changed = true;
        }

        swappedFields[addressType] = true;

        if (changed) {
            log('Field positions reorganized for', addressType);
        }

        return changed;
    }

    /**
     * Actualizar el select de comunas con nuevas opciones
     */
    function updateComunaOptions(addressType, comunas, currentValue) {
        const cityWrapper = getCityWrapper(addressType);
        if (!cityWrapper) return false;

        const select = cityWrapper.querySelector('.spcb-comuna-select');
        if (!select) return false;

        // Limpiar opciones existentes
        select.innerHTML = '';

        // Agregar placeholder
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Seleccionar comuna';
        placeholder.disabled = true;
        select.appendChild(placeholder);

        // Agregar nuevas opciones
        let hasSelected = false;
        comunas.forEach(city => {
            const option = document.createElement('option');
            option.value = city;
            option.textContent = city;
            if (city === currentValue) {
                option.selected = true;
                hasSelected = true;
            }
            select.appendChild(option);
        });

        if (!hasSelected) {
            placeholder.selected = true;
        }

        log('Updated comuna options for', addressType, 'with', comunas.length, 'comunas');
        return true;
    }

    /**
     * Inyectar select de comunas en el checkout
     */
    async function injectComunaSelect(forceUpdate = false) {
        if (isInjecting && !forceUpdate) {
            log('Already injecting, skipping');
            return false;
        }

        isInjecting = true;

        try {
            const addressTypes = ['shipping', 'billing'];
            let injected = false;

            for (const addressType of addressTypes) {
                if (!isAddressFormVisible(addressType)) {
                    continue;
                }

                const cityInput = document.querySelector(`#${addressType}-city`);
                if (!cityInput) continue;

                const cityWrapper = getCityWrapper(addressType);
                if (!cityWrapper) continue;

                // Intercambiar posiciones: Región antes de Comuna
                swapFieldPositions(addressType);

                const countrySelect = document.querySelector(`#${addressType}-country`);
                const country = countrySelect?.value || '';

                // Solo para Chile
                if (country !== 'CL') {
                    showOriginalCityInput(cityInput, cityWrapper);
                    continue;
                }

                const state = getCurrentState(addressType);

                // Verificar si la región cambió
                const stateChanged = lastKnownState[addressType] !== state;
                if (stateChanged) {
                    log('State changed for', addressType, ':', lastKnownState[addressType], '->', state);
                    lastKnownState[addressType] = state;
                }

                // Cargar comunas
                const comunas = await loadComunas(state);

                if (!comunas || comunas.length === 0) {
                    showOriginalCityInput(cityInput, cityWrapper);
                    continue;
                }

                // Si ya tenemos el container y solo cambió la región, actualizar opciones
                const existingContainer = cityWrapper.querySelector('.spcb-comuna-container');
                if (existingContainer && stateChanged) {
                    // Limpiar valor del input
                    safeSetReactInputValue(cityInput, '', 'comuna-reset');
                    // Actualizar opciones
                    updateComunaOptions(addressType, comunas, '');
                    continue;
                }

                // Si ya está inyectado y no hay cambio, saltar
                if (existingContainer && !forceUpdate) {
                    continue;
                }

                // Inyectar nuevo select
                if (cityWrapper.dataset.spcbInjecting === '1') {
                    continue;
                }
                cityWrapper.dataset.spcbInjecting = '1';

                // Ocultar input original
                cityInput.style.cssText = 'position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;white-space:nowrap!important;border:0!important;';

                const originalLabel = cityWrapper.querySelector(`label[for="${cityInput.id}"]`);
                if (originalLabel) {
                    originalLabel.style.display = 'none';
                }

                // Agregar clases
                cityWrapper.classList.add('wc-blocks-components-select');
                cityWrapper.classList.add('spcb-comuna-active');

                // Crear e insertar
                const comunaContent = createComunaContent(
                    comunas,
                    cityInput.value,
                    (value) => {
                        log('Comuna selected:', value);
                        safeSetReactInputValue(cityInput, value, 'comuna-select');
                    },
                    addressType
                );

                cityWrapper.insertBefore(comunaContent, cityWrapper.firstChild);

                delete cityWrapper.dataset.spcbInjecting;
                injected = true;
                log('Injected comuna select for:', addressType);
            }

            return injected;
        } finally {
            isInjecting = false;
        }
    }

    /**
     * Mostrar el input original de ciudad
     */
    function showOriginalCityInput(cityInput, wrapper) {
        cityInput.style.cssText = '';

        const originalLabel = wrapper.querySelector(`label[for="${cityInput.id}"]`);
        if (originalLabel) {
            originalLabel.style.display = '';
        }

        wrapper.classList.remove('wc-blocks-components-select');
        wrapper.classList.remove('spcb-comuna-active');

        const comunaContainer = wrapper.querySelector('.spcb-comuna-container');
        if (comunaContainer) {
            comunaContainer.remove();
        }
    }

    /**
     * Manejar cambio de región - simplificado
     */
    async function handleRegionChange(addressType) {
        log('Handling region change for:', addressType);

        const state = getCurrentState(addressType);
        const cityInput = document.querySelector(`#${addressType}-city`);

        if (!cityInput) return;

        // Marcar que la región cambió
        lastKnownState[addressType] = state;

        // Cargar nuevas comunas
        const comunas = await loadComunas(state);

        if (!comunas || comunas.length === 0) {
            const cityWrapper = getCityWrapper(addressType);
            if (cityWrapper) {
                showOriginalCityInput(cityInput, cityWrapper);
            }
            return;
        }

        // Limpiar valor de ciudad
        safeSetReactInputValue(cityInput, '', 'comuna-reset');

        // Actualizar opciones del select
        const updated = updateComunaOptions(addressType, comunas, '');

        if (!updated) {
            // Si no se pudo actualizar, re-inyectar
            const cityWrapper = getCityWrapper(addressType);
            if (cityWrapper) {
                const container = cityWrapper.querySelector('.spcb-comuna-container');
                if (container) container.remove();
                cityWrapper.classList.remove('spcb-comuna-active');
            }
            await injectComunaSelect(true);
        }
    }

    /**
     * Configurar listeners para cambios de país y estado
     */
    function setupFieldListeners() {
        ['shipping', 'billing'].forEach(addressType => {
            const countrySelect = document.querySelector(`#${addressType}-country`);
            const stateSelect = document.querySelector(`#${addressType}-state`);

            if (countrySelect && !countrySelect._spcbListener) {
                countrySelect.addEventListener('change', () => {
                    log('Country changed for', addressType);
                    timers.scheduleTimeout(() => handleRegionChange(addressType), 100, 'country-change');
                });
                countrySelect._spcbListener = true;
            }

            if (stateSelect && !stateSelect._spcbListener) {
                stateSelect.addEventListener('change', () => {
                    log('State changed for', addressType, 'to', stateSelect.value);
                    timers.scheduleTimeout(() => handleRegionChange(addressType), 100, 'state-change');
                });
                stateSelect._spcbListener = true;
            }
        });

        log('Field listeners setup complete');
    }

    /**
     * Verificar si la región cambió (polling)
     */
    function checkForStateChanges() {
        ['shipping', 'billing'].forEach(addressType => {
            if (!isAddressFormVisible(addressType)) return;

            const currentState = getCurrentState(addressType);
            const knownState = lastKnownState[addressType];

            if (knownState !== null && currentState !== knownState) {
                log('Detected state change via polling:', addressType, knownState, '->', currentState);
                handleRegionChange(addressType);
            }
        });
    }

    /**
     * Corregir mensaje de validación "un comuna válido" → "una comuna válida"
     */
    function fixValidationMessages() {
        const errorMessages = document.querySelectorAll('.wc-block-components-validation-error span');
        errorMessages.forEach(span => {
            if (span.textContent.includes('un comuna válido')) {
                span.textContent = span.textContent.replace('un comuna válido', 'una comuna válida');
            }
        });
    }

    /**
     * Traducir textos de WooCommerce Blocks que se renderizan via JavaScript
     * (no son capturados por el filtro gettext de PHP)
     */
    function translateBlocksTexts() {
        // Toggle de address_2 "Añadir apartamento..."
        const address2Toggles = document.querySelectorAll('.wc-block-components-address-form__address_2-toggle');
        address2Toggles.forEach(toggle => {
            if (toggle.textContent.includes('Añadir apartamento') ||
                toggle.textContent.includes('Add apartment')) {
                toggle.textContent = '+ Agregar Depto / Piso / Oficina';
            }
        });
    }

    /**
     * Re-aplicar clases cuando React las quita
     */
    function ensureClasses() {
        ['shipping', 'billing'].forEach(addressType => {
            const cityWrapper = getCityWrapper(addressType);
            if (!cityWrapper) return;

            const hasOurContainer = cityWrapper.querySelector('.spcb-comuna-container');

            if (hasOurContainer && !cityWrapper.classList.contains('spcb-comuna-active')) {
                cityWrapper.classList.add('spcb-comuna-active');
                cityWrapper.classList.add('wc-blocks-components-select');
                log('Re-applied classes for', addressType);
            }
        });
    }

    /**
     * Verificar si los campos están en el orden correcto y re-intercambiar si es necesario
     * (React puede re-renderizar y poner los campos en el orden original)
     */
    function ensureFieldOrder() {
        ['shipping', 'billing'].forEach(addressType => {
            const form = document.querySelector(`#${addressType}`);
            if (!form) return;

            const address1Wrapper = document.querySelector(`#${addressType}-address_1`)?.closest('.wc-block-components-address-form__address_1');
            const address2Wrapper = document.querySelector(`#${addressType}-address_2`)?.closest('.wc-block-components-address-form__address_2');
            const stateWrapper = document.querySelector(`#${addressType}-state`)?.closest('.wc-block-components-address-form__state');
            const cityWrapper = document.querySelector(`#${addressType}-city`)?.closest('.wc-block-components-address-form__city');

            if (!stateWrapper || !cityWrapper) return;

            let needsReorder = false;

            // Verificar si address_2 está justo después de address_1
            if (address1Wrapper && address2Wrapper) {
                const addr1Index = Array.from(form.children).indexOf(address1Wrapper);
                const addr2Index = Array.from(form.children).indexOf(address2Wrapper);

                if (addr2Index !== addr1Index + 1) {
                    needsReorder = true;
                }
            }

            // Verificar orden state/city
            const stateIndex = Array.from(form.children).indexOf(stateWrapper);
            const cityIndex = Array.from(form.children).indexOf(cityWrapper);

            if (cityIndex < stateIndex) {
                needsReorder = true;
            }

            // Si algo está fuera de orden, re-organizar
            if (needsReorder) {
                log('Fields out of order for', addressType, '- re-organizing');
                swappedFields[addressType] = false; // Reset flag
                swapFieldPositions(addressType);
            }
        });
    }

    /**
     * MutationObserver para detectar cambios en el DOM
     */
    function setupMutationObserver() {
        let debounceTimer = null;

        const observer = new MutationObserver(() => {
            if (debounceTimer) {
                timers.clearManagedTimeout(debounceTimer);
            }

            debounceTimer = timers.scheduleTimeout(() => {
                ensureClasses();
                ensureFieldOrder();
                checkForStateChanges();
                fixValidationMessages();
                translateBlocksTexts();

                ['shipping', 'billing'].forEach(addressType => {
                    if (!isAddressFormVisible(addressType)) return;

                    const cityWrapper = getCityWrapper(addressType);
                    if (!cityWrapper) return;

                    const hasOurContainer = cityWrapper.querySelector('.spcb-comuna-container');

                    if (!hasOurContainer) {
                        log('MutationObserver: need to inject for', addressType);
                        injectComunaSelect();
                    }
                });
            }, 50, 'mutation-observer-debounce');
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style', 'value']
        });

        log('MutationObserver setup complete');
    }

    /**
     * Inicialización
     */
    async function init() {
        log('Initializing v2.5.0...');

        // Precargar comunas
        preloadAllComunas();

        // Inyectar (también intercambia posiciones)
        await injectComunaSelect();
        setupFieldListeners();

        // Reintentos
        [100, 300, 600, 1000, 2000].forEach(delay => {
            timers.scheduleTimeout(() => {
                ensureFieldOrder();
                injectComunaSelect();
                setupFieldListeners();
                translateBlocksTexts();
            }, delay, 'init-retry');
        });

        // Polling para detectar cambios que no disparan eventos
        timers.scheduleInterval(checkForStateChanges, 500, 'state-polling');
    }

    // ========================================
    // INICIALIZACIÓN
    // ========================================

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    setupMutationObserver();

    document.body.addEventListener('updated_checkout', () => {
        log('updated_checkout event');
        timers.scheduleTimeout(injectComunaSelect, 50, 'updated-checkout');
    });

    document.body.addEventListener('update_checkout', () => {
        log('update_checkout event');
        timers.scheduleTimeout(injectComunaSelect, 50, 'update-checkout');
    });

    // API global
    window.spcbComunaLoader = {
        inject: injectComunaSelect,
        loadComunas: loadComunas,
        preload: preloadAllComunas,
        handleRegionChange: handleRegionChange,
        swapFieldPositions: swapFieldPositions,
        cache: comunasCache,
        version: '2.5.2'
    };

    log('Comuna loader v2.5.2 ready');

})();
